import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import {
  DescriptionDropdown,
  DescriptionDropdownItem,
  DescriptionDropdownLabel,
  HorizontalLine,
  PolicyDescriptionDropdownSection,
  PolicyDescriptionHeader,
  PolicyDescriptionHelpSection,
  PolicyDescriptionHelpText,
  PolicyDescriptionTooltipSection,
  SelectPolicyButtonsSection,
  SelectPolicyCancelButton,
  SelectPolicyDescription,
  SelectPolicyHeader,
  SelectPolicyModal,
  SelectPolicyProceedButton,
  SelectPolicyProductsContainer,
  TooltipCloseButton,
  TooltipIcon,
  TooltipText,
} from './file-claim-popup.styles';
import { ProductCard } from 'common/components/card/product-card';
import Image from 'next/image';
import { useGroupedFilteredPolicies } from 'modules/policies/hooks/use-policy-list';
import { MAX_GROUPED_POLICIES_PAGE_SIZE, PolicyStatus } from 'modules/policies/utils/constants/policy.constants';
import { DEFAULT_PAGE_NUMBER, PAGE } from 'common/utils/constants/page.constants';
import { Loader } from '@sapiens/insured-component-library';
import Error from 'common/components/error/errors';
import { PolicyLineDetail } from 'modules/policies/types/policy.types';
import { Portfolio } from 'modules/sales-policies/types/sales-portfolio.interfaces';
import { checkRecentClaims } from 'common/utils/helpers/policies.helpers';
import { useRouter } from 'next/router';
import { FNOLJOURNEYS } from 'common/utils/constants/journey.constants';
import SalesPortfolioService from 'modules/sales-policies/services/sales-portfolio.service';
import getConfig from 'next/config';
import { usePortfolioStore } from 'common/stores/sales-portfolio-store';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export interface FileClaimPopupInterface {
  showPopup: boolean;
  setShowPopup: Dispatch<SetStateAction<boolean>>;
}

export const FileClaimPopup: FC<FileClaimPopupInterface> = ({ showPopup, setShowPopup }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [enableProceed, setEnableProceed] = useState(false);
  const [seletedPolicyLineDetails, setSelectedPolicyLinesDetails] = useState<PolicyLineDetail[] | undefined>([]);
  const [selectedPolicyLineNo, setSelectedPolicyLineNo] = useState('');
  const { data, isError, isLoading, error } = useGroupedFilteredPolicies(
    PolicyStatus.ACTIVE,
    DEFAULT_PAGE_NUMBER,
    MAX_GROUPED_POLICIES_PAGE_SIZE
  );
  const setSelectedPortfolio = usePortfolioStore((state) => state.setPortfolio);
  const router = useRouter();
  const { publicRuntimeConfig } = getConfig();
  const { t } = useTranslation();

  const handleClick = async (policyNo: string, productId: string | undefined, portfolio: Portfolio | undefined) => {
    if (portfolio?.objectGroupSummaries.length === 1) {
      const salesProductName = portfolio.salesProductName.split('_')[1].toLocaleUpperCase();

      const recentClaimRegistered = await checkRecentClaims(router, policyNo, portfolio.policyLineNo);

      if (recentClaimRegistered) return;

      if (Object.keys(FNOLJOURNEYS).includes(salesProductName)) {
        sessionStorage.setItem('start-fnol', 'true');
      } else {
        try {
          const response = await SalesPortfolioService.getProductDetails(salesProductName);
          if (response) {
            window.open(response.redirectTo);
            return;
          }
        } catch (error) {
          window.open(`${publicRuntimeConfig.lankredittUrl}`);
        }
        window.open(`${publicRuntimeConfig.lankredittUrl}`);
        return;
      }
    }

    const pageRoute = `/${PAGE.POLICIES}/${policyNo}/${portfolio?.uuid}`;

    const qParams = {
      productId,
      policyStatus: PolicyStatus.ACTIVE,
      policyLineNo: portfolio?.policyLineNo,
      pageNo: 1,
      lineStatus: PolicyStatus.ACTIVE,
      linePageNo: 1,
    };

    setSelectedPortfolio(portfolio!);

    router
      .push({
        pathname: pageRoute,
        query: portfolio?.objectGroupSummaries.length !== 1 ? { ...qParams, help: true } : qParams,
      })
      .then(() => router.reload());
  };

  const getPolicyLinesByProductName = (salesProductName: string) => {
    return data?.productGroupedPolicies.filter((product) => product.salesProductName === salesProductName)[0]
      ?.policyLineDetails;
  };

  const getPolicyLine = (policyLineNo: string) => {
    return seletedPolicyLineDetails?.filter((policyLine) => policyLine.policyLineNo === policyLineNo)[0];
  };

  useEffect(() => {
    if (!isLoading && data) {
      setSelectedProduct(data.productGroupedPolicies[0]?.salesProductName);
    }
  }, [isLoading]);

  useEffect(() => {
    const policyLines = getPolicyLinesByProductName(selectedProduct);
    setSelectedPolicyLinesDetails(policyLines);
    setSelectedPolicyLineNo(policyLines?.length === 1 ? policyLines[0].policyLineNo : '');
    setEnableProceed(policyLines?.length === 1);
  }, [selectedProduct]);

  return (
    <SelectPolicyModal visible={showPopup} onClose={() => setShowPopup(false)}>
      {isError ? (
        <ErrorContainer>
          <p>{t(error?.message || '', t('errors.somethingWentWrong'))}</p>
        </ErrorContainer>
      ) : isLoading ? (
        <Loader />
      ) : (
        <>
          <SelectPolicyHeader>{t('fileClaimPopup.selectProductHeader')}</SelectPolicyHeader>
          <SelectPolicyProductsContainer>
            {data?.productGroupedPolicies.map((groupedProduct) => (
              <ProductCard
                product={groupedProduct}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                key={groupedProduct.salesProductName}
                imageSrc={`/icons/${groupedProduct.productId}.svg`}
              />
            ))}
          </SelectPolicyProductsContainer>
          <HorizontalLine />
          <SelectPolicyDescription>
            <PolicyDescriptionHeader>{t('fileClaimPopup.selectDescriptionHeader')}</PolicyDescriptionHeader>
          </SelectPolicyDescription>
          <PolicyDescriptionDropdownSection>
            <DescriptionDropdownLabel id="policies-description-label" htmlFor="policies-description">
              {t('fileClaimPopup.descriptionLabel')}
            </DescriptionDropdownLabel>
            <DescriptionDropdown
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                getContentAnchorEl: null,
                PaperProps: {
                  style: {
                    zIndex: 9999,
                  },
                },
              }}
              disableUnderline
              labelId="policies-description-label"
              id="policies-description"
              value={selectedPolicyLineNo}
              label="policyLineNo"
              onChange={(event) => {
                setEnableProceed(true);
                setSelectedPolicyLineNo(event.target.value as string);
              }}
            >
              {seletedPolicyLineDetails?.map((policyLine, index) => (
                <DescriptionDropdownItem
                  key={index}
                  value={policyLine.policyLineNo}
                  selected={policyLine.policyLineNo === selectedPolicyLineNo}
                  isFocused={false}
                >
                  {policyLine.description}
                </DescriptionDropdownItem>
              ))}
            </DescriptionDropdown>
            <PolicyDescriptionHelpSection>
              <PolicyDescriptionHelpText>{t('fileClaimPopup.tooltipQuestion')}</PolicyDescriptionHelpText>
              <TooltipIcon onClick={() => setShowTooltip(true)}>
                <Image src={'/icons/tooltip-icon.svg'} alt="start-date" width="20" height="20" />
              </TooltipIcon>
            </PolicyDescriptionHelpSection>
            {showTooltip && (
              <PolicyDescriptionTooltipSection>
                <TooltipCloseButton onClick={() => setShowTooltip(false)}>
                  <Image src={'/icons/close.svg'} alt="start-date" width="15" height="15" />
                </TooltipCloseButton>
                <TooltipText>{t('fileClaimPopup.tooltipAnswer')}</TooltipText>
              </PolicyDescriptionTooltipSection>
            )}
          </PolicyDescriptionDropdownSection>
          <SelectPolicyButtonsSection>
            <SelectPolicyCancelButton onClick={() => setShowPopup(false)}>
              {t('fileClaimPopup.cancelButton')}
            </SelectPolicyCancelButton>
            <SelectPolicyProceedButton
              $enableProceed={enableProceed}
              onClick={() => {
                if (enableProceed) {
                  const policyLine = getPolicyLine(selectedPolicyLineNo);
                  handleClick(policyLine?.policyNo + '', policyLine?.productId, policyLine?.portfolio);
                  setShowPopup(false);
                }
              }}
            >
              {t('fileClaimPopup.proceedButton')}
            </SelectPolicyProceedButton>
          </SelectPolicyButtonsSection>
        </>
      )}
    </SelectPolicyModal>
  );
};

const ErrorContainer = styled.div`
  width: fit-content;
  margin: 0 auto;
`;
